import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Car, Users, Briefcase, Eye, EyeOff } from "lucide-react";
import VehicleRates from "@/components/VehicleRates";
import RateScheduler from "@/components/RateScheduler";

/** Dispatcher: switch vehicle classes on/off for new reservations — live, no redeploy. */
export default function FleetTab() {
  const [vehicles, setVehicles] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = () => api.get("/vehicles/all").then(r => setVehicles(r.data)).catch(e => toast.error(e?.response?.data?.detail || "Failed to load fleet"));
  useEffect(() => { load(); }, []);

  const patch = async (v, body, msg) => {
    setBusy(v.vehicle_id);
    try {
      const r = await api.patch(`/vehicles/${v.vehicle_id}`, body);
      setVehicles(list => list.map(x => x.vehicle_id === v.vehicle_id ? { ...x, ...r.data } : x));
      toast.success(msg);
    } catch (e) { toast.error(e?.response?.data?.detail || "Update failed"); }
    finally { setBusy(null); }
  };

  if (!vehicles) return <div className="text-xs text-[#A1A1AA]">Loading fleet…</div>;
  const activeCount = vehicles.filter(v => v.active).length;

  return (
    <section data-testid="fleet-tab" className="pb-20 px-6 md:px-12">
      <div className="max-w-[1400px] mx-auto">
      <div className="flex flex-wrap justify-between items-end gap-4 mb-6">
        <div>
          <h2 className="font-serif text-2xl">Fleet &amp; Vehicle Classes</h2>
          <p className="text-xs text-[#A1A1AA] mt-1">
            Inactive vehicles vanish from the booking form, passenger portal, quotes and the rider app immediately.
            Rate edits apply to every new quote instantly; existing reservations keep their quoted fare.
          </p>
        </div>
        <div className="overline" data-testid="fleet-active-count">{activeCount} of {vehicles.length} active</div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {vehicles.map(v => {
          const on = v.active !== false;
          return (
            <div key={v.vehicle_id} data-testid={`fleet-vehicle-${v.vehicle_id}`}
              className={`border bg-[#0A0A0A] p-4 flex gap-4 transition-opacity ${on ? "border-white/10" : "border-red-500/30 opacity-70"}`}>
              {v.image_url
                ? <img src={v.image_url} alt="" className="w-24 h-20 object-cover shrink-0 bg-black"/>
                : <div className="w-24 h-20 bg-white/5 flex items-center justify-center shrink-0"><Car className="text-[#71717A]"/></div>}
              <div className="flex-1 min-w-0">
                <div className="flex items-start justify-between gap-2">
                  <div className="min-w-0">
                    <div className="font-serif text-lg text-[#F5F2EB] truncate">{v.name}</div>
                    <div className="text-[11px] text-[#A1A1AA] truncate">{v.subtitle}</div>
                  </div>
                  <span data-testid={`fleet-status-${v.vehicle_id}`}
                    className={`shrink-0 text-[9px] uppercase tracking-[0.18em] border px-2 py-0.5 ${on ? "border-emerald-500/40 text-emerald-400" : "border-red-500/40 text-red-400"}`}>
                    {on ? "Active" : "Inactive"}
                  </span>
                </div>
                <div className="flex items-center gap-3 mt-2 text-[10px] text-[#71717A] uppercase tracking-[0.16em]">
                  <span className="inline-flex items-center gap-1"><Users size={11}/>{v.pax}</span>
                  <span className="inline-flex items-center gap-1"><Briefcase size={11}/>{v.luggage}</span>
                  {v.staff_only && <span className="text-[#D4AF37]">Staff only</span>}
                  {v.open_bookings > 0 && <span data-testid={`fleet-open-${v.vehicle_id}`}>{v.open_bookings} open trips</span>}
                </div>
                <div className="flex items-center gap-2 mt-3">
                  <button
                    data-testid={`fleet-toggle-${v.vehicle_id}`}
                    disabled={busy === v.vehicle_id}
                    onClick={() => patch(v, { active: !on }, on ? `${v.name} hidden from new bookings` : `${v.name} is bookable again`)}
                    className={`px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border inline-flex items-center gap-1.5 disabled:opacity-40 ${on ? "border-red-500/40 text-red-400 hover:bg-red-500/10" : "border-emerald-500/40 text-emerald-400 hover:bg-emerald-500/10"}`}
                  >{on ? <><EyeOff size={11}/> Set inactive</> : <><Eye size={11}/> Set active</>}</button>
                  <button
                    data-testid={`fleet-staff-toggle-${v.vehicle_id}`}
                    disabled={busy === v.vehicle_id}
                    onClick={() => patch(v, { staff_only: !v.staff_only }, v.staff_only ? `${v.name} now visible to the public` : `${v.name} limited to dispatcher bookings`)}
                    className="px-3 py-1.5 text-[10px] uppercase tracking-[0.2em] border border-white/15 text-[#A1A1AA] hover:text-[#F5F2EB] disabled:opacity-40"
                  >{v.staff_only ? "Make public" : "Staff only"}</button>
                </div>
                {v.rate_schedules?.length > 0 && (
                  <div className="flex flex-wrap gap-1.5 mt-3" data-testid={`fleet-schedules-${v.vehicle_id}`}>
                    {v.rate_schedules.slice(0, 3).map(w => (
                      <span key={`${w.schedule_id}-${w.start}`} data-testid={`fleet-schedule-badge-${v.vehicle_id}-${w.schedule_id}`}
                        title={`${w.name}: ${w.start} → ${w.end}`}
                        className={`text-[9px] uppercase tracking-[0.14em] border px-2 py-0.5 ${w.status === "active" ? "border-emerald-500/40 text-emerald-400" : "border-[#D4AF37]/40 text-[#D4AF37]"}`}>
                        {w.status === "active" ? "Now" : new Date(w.start + "T12:00:00").toLocaleDateString([], { month: "short", day: "numeric" })} · {w.name} {w.pct > 0 ? "+" : ""}{w.pct}%
                      </span>
                    ))}
                    {v.rate_schedules.length > 3 && <span className="text-[9px] text-[#71717A]">+{v.rate_schedules.length - 3} more</span>}
                  </div>
                )}
                <VehicleRates v={v} onSaved={(doc) => setVehicles(list => list.map(x => x.vehicle_id === v.vehicle_id ? { ...x, ...doc, last_rate_change: doc.rate_changes?.[0] || x.last_rate_change } : x))}/>
              </div>
            </div>
          );
        })}
      </div>
      <RateScheduler vehicles={vehicles}/>
      </div>
    </section>
  );
}
